import type { ReactElement } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router'

import { Button } from '#design-system/component/Button/Button'
import { Input } from '#design-system/component/Form/Input/Input'
import { Banner } from '#design-system/component/Layout/Banner/Banner'
import { useAuth } from '~/common/hooks/useAuth'
import { getAuthRoute } from '~/common/utils/auth-routes'

import { AuthLayout } from './AuthLayout'

export const AcceptInvitation = (): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signIn } = useAuth()

  // Invitation token comes in as token parameter from email link
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Show error if no token provided
  if (!token) {
    return (
      <AuthLayout type="accept-invitation">
        <Banner
          bodyText={t('auth.invalid_invitation_link_body')}
          headerText={t('auth.invalid_invitation_link')}
          semanticType="Error"
        />
        <div className="footer">
          <Link to="/auth/login">{t('auth.back_to_login')}</Link>
        </div>
      </AuthLayout>
    )
  }

  const handleSubmit = async (): Promise<void> => {
    setError(null)
    setIsSubmitting(true)

    try {
      const response = await fetch(getAuthRoute('accept-invitation'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ inviteToken: token, password }),
      })

      const data = (await response.json()) as { email?: string; message?: string }

      if (!response.ok) {
        throw new Error(data.message ?? t('auth.accept_invitation_failed'))
      }

      // Get email from response to auto-sign in
      const { email } = data

      if (!email) {
        throw new Error(t('auth.accept_invitation_failed'))
      }

      // Auto-sign in the user with their new password
      await signIn(email, password)

      // Redirect to home after successful sign in
      void navigate('/', { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.accept_invitation_failed')
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthLayout title={t('auth.create_your_account_subtitle')} type="accept-invitation">
      {error && <Banner bodyText={error} headerText={t('auth.error')} semanticType="Error" />}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
      >
        <Input
          autoComplete="new-password"
          helperText={t('auth.password_requirements')}
          label={t('auth.new_password')}
          minLength={8}
          onChange={setPassword}
          onLiveChange={setPassword}
          required
          size="full"
          type="password"
          value={password}
        />

        <Button
          actionRole="primary"
          disabledMessage={isSubmitting ? t('auth.setting_up') : undefined}
          label={t('auth.create_my_account')}
          type="submit"
        />
      </form>

      <p className="terms">
        {t('auth.terms_agreement')}{' '}
        <a href="https://www.doss.com/privacy-policy" rel="noreferrer" target="_blank">
          {t('auth.terms_link')}
        </a>
      </p>

      <div className="footer">
        {t('auth.already_have_account')} <Link to="/auth/login">{t('auth.sign_in')}</Link>
      </div>
    </AuthLayout>
  )
}
